import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export type Dealer = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  address: string | null;
  town: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  website_url: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  discord_url: string | null;
  /** Contact fields are only present for owners/admins (base table reads). */
  email?: string | null;
  phone?: string | null;
  opening_hours: Record<string, string> | null;
  categories: string[];
  supported_games: string[];
  is_active: boolean;
  is_featured: boolean;
  linked_partner_id: string | null;
  linked_seller_id: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
};

export const DEALER_CATEGORY_LABELS: Record<string, string> = {
  brick_and_mortar: "Brick & Mortar Store",
  online_retailer: "Online Retailer",
  hobby_shop: "Hobby Shop",
  trading_card_shop: "Trading Card Shop",
  grading_service: "Grading Company",
  accessories_store: "Accessories Store",
  breaker: "Breaker",
  streamer: "Streamer",
  content_creator: "Content Creator",
  event_organiser: "Event Organiser",
  auction_house: "Auction House",
};


export async function fetchActiveDealers(limit = 60): Promise<Dealer[]> {
  const { data, error } = await supabase
    .from("public_dealers" as never)
    .select("*")
    .eq("is_active", true)
    .order("is_featured", { ascending: false })
    .order("display_order", { ascending: true })
    .order("name", { ascending: true })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as unknown as Dealer[];
}

export async function fetchFeaturedDealers(limit = 4): Promise<Dealer[]> {
  const { data, error } = await supabase
    .from("public_dealers" as never)
    .select("*")
    .eq("is_active", true)
    .eq("is_featured", true)
    .order("display_order", { ascending: true })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as unknown as Dealer[];
}

export async function fetchDealerBySlug(slug: string): Promise<Dealer | null> {
  const { data, error } = await supabase
    .from("public_dealers" as never)
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) return null;
  return (data as unknown as Dealer) ?? null;
}

const DealerInput = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(200),
  slug: z.string().trim().min(2).max(200).regex(/^[a-z0-9-]+$/i),
  description: z.string().trim().max(4000).optional().nullable(),
  logo_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  banner_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  address: z.string().trim().max(300).optional().nullable(),
  town: z.string().trim().max(120).optional().nullable(),
  city: z.string().trim().max(120).optional().nullable(),
  county: z.string().trim().max(120).optional().nullable(),
  postcode: z.string().trim().max(20).optional().nullable(),
  country: z.string().trim().max(80).default("United Kingdom"),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  website_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  facebook_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  instagram_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  discord_url: z.string().trim().url().max(500).optional().nullable().or(z.literal("")),
  email: z.string().trim().email().max(320).optional().nullable().or(z.literal("")),
  phone: z.string().trim().max(40).optional().nullable(),
  opening_hours: z.record(z.string(), z.string()).default({}),
  categories: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  supported_games: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
  is_active: z.boolean().default(true),
  is_featured: z.boolean().default(false),
  display_order: z.number().int().min(0).max(10000).default(0),
});

export const listAllDealers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("dealers")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Dealer[];
  });

export const upsertDealer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => DealerInput.parse(d))
  .handler(async ({ data, context }) => {
    const clean = (v?: string | null) => (v ? v : null);
    const payload = {
      ...data,
      logo_url: clean(data.logo_url),
      banner_url: clean(data.banner_url),
      website_url: clean(data.website_url),
      facebook_url: clean(data.facebook_url),
      instagram_url: clean(data.instagram_url),
      discord_url: clean(data.discord_url),
      email: clean(data.email),
    };
    const { id, ...rest } = payload;
    if (id) {
      const { error } = await context.supabase
        .from("dealers")
        .update(rest as never)
        .eq("id", id);
      if (error) throw new Error(error.message);
      return { ok: true, id };
    }
    const { data: row, error } = await context.supabase
      .from("dealers")
      .insert(rest as never)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id };
  });

export const deleteDealer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("dealers").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
